import { Injectable, inject } from '@angular/core';
import { CrudQuery, CrudService } from '@shared/modules/crud/crud.service';
import { FirebaseUploaderService } from '@shared/services/firebase-uploader.service';
import { Observable, firstValueFrom, map } from 'rxjs';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
export interface Change {
    before: string;
    after: string;
}
export interface History {
    uid: string;
    createdAt: Date;
    changes: Record<string, Change>;
}
@Injectable({
    providedIn: 'root',
})
export class UsersService {
    private http = inject(HttpClient);
    private functions: Functions = inject(Functions);

    model = 'users';
    endpoint = environment.backendUrl;
    crudService = inject(CrudService);
    uploaderService = inject(FirebaseUploaderService);
    getOne(id: string, type = 'customer') {
        const path = this.model + '/' + id + '/accounts/' + type;
        this.crudService.setModel(path);
        return this.crudService.findFirst<any>('');
    }
    search(id: string, type = 'customer') {
        const path = this.model + '/' + id + '/accounts/' + type;
        this.crudService.setModel(path);
        const query: CrudQuery = {
            field: '',
            operator: '==',
            value: '',
            orderBy: '',
            orderByDirection: 'asc',
        };
        return this.crudService.search<any>(query);
    }

    cuitExists(cuit: string, type = 'customer'): Observable<boolean> {
        const path = 'accounts';
        this.crudService.setModel(path);
        const query: CrudQuery = {
            field: 'cuit',
            operator: '==',
            value: cuit,
            orderBy: 'createdAt',
            orderByDirection: 'asc',
        };
        return this.crudService.searchCollectionGroup<any>(query).pipe(
            map((x) => x.filter((y) => y.type === type)),
            map((x) => x.length > 0)
        );
    }
    create(id: string | number, user: any, type = 'customer') {
        const path = this.model + '/' + id + '/accounts/' + type;
        user.parentUid = id + '';
        this.crudService.setModel(path);
        return this.crudService.create('', user);
    }
    update(id: string, user: any, type = 'customer') {
        const path = this.model + '/' + id + '/accounts/' + type;
        this.crudService.setModel(path);
        return this.crudService.update('', user);
    }

    async delete(id: string, type = 'customer') {
        const callable = httpsCallable(this.functions, 'removeuser');
        try {
            const payload = {
                uid: id,
                type,
            };
            await callable(payload);
        } catch (error) {
           // console.log(error);
        }
    }

    getAll() {
        const path = 'accounts';
        this.crudService.setModel(path);
        return this.crudService.findAllCollectionGroup<any>();
    }

    getHistory(id: string, type = 'customer') {
        const path = this.model + '/' + id + '/accounts/' + type + '/historian';
        this.crudService.setModel(path);
        return this.crudService
            .findAll<History>()
            .pipe(map((x) => x.reverse()));
    }

    async upload(image: any, id: string, type: string) {
        if (!image) return;
        const base64 = image.base64.split(',')[1];
        const upload = await this.uploaderService.uploadFileFromBase64(
            base64,
            'users/' + id + '/' + image.name
        );
        if (upload) {
            await this.update(
                id + '',
                {
                    imageUrl: upload,
                },
                type
            );
        }
    }
    async uploadDni(image: any, id: string, type: string, dniType: string) {
        if (!image) return;
        const doc = await firstValueFrom(this.getOne(id, type));
        const currentDocuments = doc.documents;
        const base64 = image.base64.split(',')[1];
        const upload = await this.uploaderService.uploadFileFromBase64(
            base64,
            'users/' + id + '/' + image.name
        );
        if (upload) {
            if (dniType === 'dniFront') {
                await this.update(
                    id + '',
                    {
                        documents: {
                            ...currentDocuments,
                            dniFront: upload,
                            nameDniFront: image.name,
                        },
                    },
                    type
                );
            } else if (dniType === 'dniBack') {
                await this.update(
                    id + '',
                    {
                        documents: {
                            ...currentDocuments,
                            dniBack: upload,
                            nameDniBack: image.name,
                        },
                    },
                    type
                );
            }
        }
    }

    async updateEmail(id: string, email: string, type: string) {
        return await firstValueFrom(
            this.http.get<any>(
                `${this.endpoint}/updateemailverified?code=${id}&email=${email}&userType=${type}`
            )
        );
    }
}
